import { apiClient } from './client'
import { Group, ApiResponse } from '@types'

/**
 * Paths here mirror apps/groups/urls.py, which is mounted at /api/v1/:
 * the GroupViewSet lives at /groups/, and member/share management is done
 * through its detail actions (join, leave, invite_member, manage_member)
 * or the sibling /hive-shares/ collection - not through nested
 * /groups/{id}/users/... routes, which the backend does not serve.
 */
export const groupsApi = {
  getGroups: () => apiClient.get<ApiResponse<Group[]>>('/groups/'),

  getGroup: (id: number) => apiClient.get<ApiResponse<Group>>(`/groups/${id}/`),

  createGroup: (data: Partial<Group>) =>
    apiClient.post<ApiResponse<Group>>('/groups/', data),

  updateGroup: (id: number, data: Partial<Group>) =>
    apiClient.patch<ApiResponse<Group>>(`/groups/${id}/`, data),

  deleteGroup: (id: number) => apiClient.delete(`/groups/${id}/`),

  inviteUser: (groupId: number, email: string, message?: string) =>
    apiClient.post(`/groups/${groupId}/invite_member/`, { email, message: message || '' }),

  // `membershipId` is the id from Group.members[] (a GroupMembership row),
  // not a user id - that is what manage_member expects.
  removeUser: (groupId: number, membershipId: number) =>
    apiClient.post(`/groups/${groupId}/manage_member/`, {
      member_id: membershipId,
      action: 'remove',
    }),

  changeMemberRole: (groupId: number, membershipId: number, newRole: string) =>
    apiClient.post(`/groups/${groupId}/manage_member/`, {
      member_id: membershipId,
      action: 'change_role',
      new_role: newRole,
    }),

  getMembers: (groupId: number) => apiClient.get(`/groups/${groupId}/members/`),

  joinGroup: (groupId: number, message?: string) =>
    apiClient.post(`/groups/${groupId}/join/`, { message: message || '' }),

  leaveGroup: (groupId: number) => apiClient.post(`/groups/${groupId}/leave/`),

  // Accept or decline an emailed invitation by its token.
  respondToInvitation: (token: string, action: 'accept' | 'decline') =>
    apiClient.post('/invitations/respond/', { token, action }),

  getGroupHives: (groupId: number) =>
    apiClient.get('/hive-shares/', { params: { group: groupId } }),

  shareHiveWithGroup: (
    groupId: number,
    payload: { hive_id: number; can_edit?: boolean; can_view_details?: boolean }
  ) =>
    apiClient.post('/hive-shares/', {
      group: groupId,
      hive: payload.hive_id,
      can_edit: payload.can_edit ?? false,
      can_view_details: payload.can_view_details ?? true,
    }),

  // `shareId` is the GroupHiveShare id, from getGroupHives().
  unshareHiveFromGroup: (shareId: number) =>
    apiClient.delete(`/hive-shares/${shareId}/`),
}
